import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import EnergyReportPanel from "./EnergyReportPanel";
import { foyerApi } from "../http/foyer";
import type { EnergyRange, EnergyReport } from "@shared/types/foyer";

const RANGE_OPTIONS: { label: string; value: EnergyRange }[] = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
];

/**
 * Chain-level energy + CO₂ rollup for one company, across all its premises.
 * Launched from the Companies list - the group ESG view a hotel chain's
 * sustainability lead wants, distinct from the per-property view in the premise
 * editor. Reads the same endpoint scoped by `companyId`.
 */
export default function CompanyEnergyDialog({
  open,
  companyId,
  companyName,
  onClose,
}: {
  open: boolean;
  companyId: string | null;
  companyName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<EnergyReport | null>(null);
  const [range, setRange] = useState<EnergyRange>("week");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    async function load() {
      try {
        const r = await foyerApi.fleetEnergy(range, {
          companyId: companyId!,
        });
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, range]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Energy - {companyName}</Typography>
          <Typography variant="caption" color="text.secondary">
            Chain rollup across every premise in this company.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={range}
            onChange={(e) => setRange(e.target.value as EnergyRange)}
            sx={{ minWidth: 120 }}
            data-testid="company-energy-range"
          >
            {RANGE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {data ? (
          <EnergyReportPanel report={data} scope="company" />
        ) : (
          !error && (
            <Grid container spacing={2.5}>
              {[0, 1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{ p: 3 }}>
                    <Box sx={{ height: 88 }} />
                  </Card>
                </Grid>
              ))}
            </Grid>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
